/**
 * Arquivo: src/components/MobileActionSheet.tsx
 * Versão: 5.6.5
 * Data: 03/10/2026
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { protegerAposFechamentoPorToque } from '../utils/touchInteractionGuard'

export type MobileAction = {
  label: string
  icon?: ReactNode
  danger?: boolean
  disabled?: boolean
  /**
   * Quando true, a ação abre um segundo nível do mesmo fluxo
   * (detalhes, edição, anexos, confirmação etc.). Nesse caso,
   * o action sheet deve apenas sair da frente, sem encerrar a origem.
   */
  continuaFluxo?: boolean
  onClick: () => void
}

type Props = {
  aberto: boolean
  titulo: string
  subtitulo?: string
  actions: MobileAction[]
  onClose: () => void
  /** Fecha somente o action sheet durante uma transição interna. */
  onActionTransition?: () => void
}

export default function MobileActionSheet({ aberto, titulo, subtitulo, actions, onClose, onActionTransition }: Props) {
  const fecharRef=useRef(onClose)
  const botaoFecharRef=useRef<HTMLButtonElement>(null)
  fecharRef.current=onClose
  useEffect(()=>{
    if(!aberto)return
    botaoFecharRef.current?.focus()
    const escape=(event:KeyboardEvent)=>{
      if(event.key==='Escape'){
        event.preventDefault()
        event.stopImmediatePropagation()
        fecharRef.current()
      }
    }
    window.addEventListener('keydown',escape,true)
    return()=>window.removeEventListener('keydown',escape,true)
  },[aberto])
  if (!aberto) return null
  return <>
    <button
      className="mobile-sheet-overlay record-sheet-overlay"
      aria-label="Fechar"
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        protegerAposFechamentoPorToque(event.detail)
        onClose()
      }}
    />
    <section
      className="mobile-action-sheet record-action-sheet"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      style={{ maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto', scrollbarGutter: 'auto' }}
    >
      <div className="mobile-sheet-handle" />
      <div className="mobile-sheet-header">
        <div>
          <strong>{titulo}</strong>
          {subtitulo && <span className={subtitulo.includes('R' + '$') ? 'privacy-value' : undefined}>{subtitulo}</span>}
        </div>
        <button ref={botaoFecharRef} className="mobile-sheet-close" onClick={(event) => { event.preventDefault(); event.stopPropagation(); protegerAposFechamentoPorToque(event.detail); onClose() }} aria-label="Fechar"><X size={18}/></button>
      </div>
      <div className="record-action-list" style={{ overflow: 'visible' }}>
        {actions.map((action, index) => <button
          key={`${action.label}-${index}`}
          className={action.danger ? 'danger' : ''}
          disabled={action.disabled}
          onClick={() => {
            if(action.continuaFluxo && onActionTransition) onActionTransition()
            else onClose()
            action.onClick()
          }}
        >
          <span>{action.icon}</span><strong>{action.label}</strong>
        </button>)}
      </div>
    </section>
  </>
}
